import React from 'react'
import { cn } from 'components/UI/cn'

export type FormattedLabelVariant =
  | 'breadcrumb' // Main part normal, suffix light weight
  | 'dropdown' // Main part normal, suffix extralight italic
  | 'nav-item' // Main part with active state, suffix extralight

interface FormattedLabelProps {
  label: string
  variant?: FormattedLabelVariant
  isActive?: boolean
  className?: string
  mainClassName?: string
  suffixClassName?: string
}

/**
 * Renders a label with proper styling for suffix after " - "
 * Supports multiple variants for different contexts:
 * - breadcrumb: "Healthcare - Hospitals" with light suffix
 * - dropdown: "Education - K-12" with extralight italic suffix
 * - nav-item: With active state support and extralight suffix
 */
export const FormattedLabel: React.FC<FormattedLabelProps> = ({
  label,
  variant = 'breadcrumb',
  isActive = false,
  className,
  mainClassName,
  suffixClassName,
}) => {
  const dashIndex = label.indexOf(' - ')

  // No dash found, render plain label
  if (dashIndex === -1) {
    if (variant === 'nav-item') {
      return (
        <span
          className={cn(
            'text-[13px] text-[#191d23] leading-none',
            isActive ? 'font-medium' : 'font-light',
            className
          )}
        >
          {label}
        </span>
      )
    }
    return <span className={className}>{label}</span>
  }

  const mainText = label.substring(0, dashIndex)
  const suffix = label.substring(dashIndex) // includes " - "

  // Variant-specific styling
  const getMainStyles = () => {
    switch (variant) {
      case 'nav-item':
        return cn(
          'text-[13px] text-[#191d23] leading-none align-baseline',
          isActive ? 'font-medium' : 'font-light',
          mainClassName
        )
      case 'dropdown':
      case 'breadcrumb':
      default:
        return mainClassName
    }
  }

  const getSuffixStyles = () => {
    switch (variant) {
      case 'dropdown':
        return cn('font-extralight italic', suffixClassName)
      case 'nav-item':
        return cn(
          'text-[11px] font-extralight italic text-[#191d23] leading-none align-baseline',
          suffixClassName
        )
      case 'breadcrumb':
      default:
        return cn('font-light', suffixClassName)
    }
  }

  if (variant === 'nav-item') {
    return (
      <span className="leading-tight">
        <span className={getMainStyles()}>{mainText}</span>
        <span className={getSuffixStyles()}>{suffix}</span>
      </span>
    )
  }

  return (
    <span className={cn('inline', className)}>
      <span className={getMainStyles()}>{mainText}</span>
      <span className={getSuffixStyles()}>{suffix}</span>
    </span>
  )
}
